import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import Avatar from '@/components/Avatar';
import PersonIdentity from '@/components/PersonIdentity';
import Heartbeat from '@/components/Heartbeat';
import Sparkline from '@/components/Sparkline';
import { useOrg, ALL_ORGS } from '@/context/OrgContext';
import { useData } from '@/lib/useData';
import { safeId, formatRelative } from '@/lib/format';
import { smoothPath } from '@/lib/smoothPath';
import {
  heroTiles, wallRows, buildCards, feedEvents,
  type Delta, type HeroTile, type WallRow, type CardDef, type CardRow, type FeedEvent,
} from '@/lib/derive';

export default function Leaderboard() {
  const data = useData();
  const { org, label } = useOrg();
  const days = data.calendar[org]?.days ?? [];
  const heartbeat = data.heartbeat[org] ?? [];

  const hero    = useMemo(() => heroTiles(days),           [days]);
  const wall    = useMemo(() => wallRows(days, 60),        [days]);
  const cards   = useMemo(() => buildCards(data, org),     [data, org]);
  const feed    = useMemo(() => feedEvents(days, 20),      [days]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-[28px] sm:text-[32px] font-bold tracking-tight-plus text-fg leading-[1.1]">
          Shipping at 5C, together
        </h1>
        <p className="mt-1 text-[13px] text-fg-muted">
          {org === ALL_ORGS
            ? 'Across every org. No rankings. Many ways to ship.'
            : `${label(org)} — no rankings, many ways to ship.`}
        </p>
      </header>

      {/* HERO TILES --------------------------------------------------- */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {hero.map((h) => <HeroTileCard key={h.label} tile={h} />)}
      </section>

      {/* HEARTBEAT ---------------------------------------------------- */}
      <section className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle">
            Heartbeat | commits per day, last 90 days
          </div>
          <span className="text-[11px] text-fg-subtle">{org === ALL_ORGS ? 'org-wide' : label(org)}</span>
        </div>
        <Heartbeat days={heartbeat} />
      </section>

      {/* THE WALL ----------------------------------------------------- */}
      <section className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle">
            The Wall | last 90 days, per active contributor
          </div>
          <span className="text-[11px] text-fg-subtle">
            {wall.length} people | most-recently-active first
          </span>
        </div>
        {wall.length === 0 ? (
          <p className="text-[12px] text-fg-subtle py-6 text-center">No activity in the last 90 days.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-0.5">
            {wall.map((w) => <WallRowItem key={w.canonical_id} row={w} />)}
          </div>
        )}
      </section>

      {/* LEADERBOARD CARDS ------------------------------------------- */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {cards.map((c) => <BoardCard key={c.title} card={c} />)}
      </section>

      {/* LAST 48H FEED ----------------------------------------------- */}
      <section className="card p-4">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle mb-3">
          Last 48 hours | merged PRs
        </div>
        {feed.length === 0 ? (
          <p className="text-[12px] text-fg-subtle py-4">No PRs merged in the last 48 hours.</p>
        ) : (
          <ul className="space-y-2">
            {feed.map((e, i) => <FeedRow key={`${e.org}/${e.repo}#${e.number}-${i}`} e={e} />)}
          </ul>
        )}
      </section>
    </div>
  );
}

// --- HERO TILE --------------------------------------------------------------

function HeroTileCard({ tile }: { tile: HeroTile }) {
  return (
    <div className="card p-3.5">
      <div className="text-[11px] uppercase tracking-wider font-medium text-fg-subtle">{tile.label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-[26px] font-semibold num tracking-tight-plus text-fg leading-none">
          {tile.value.toLocaleString()}
        </span>
        <DeltaBadge d={tile.delta} />
      </div>
    </div>
  );
}

function DeltaBadge({ d }: { d: Delta }) {
  if (d.arrow === 'flat') return <span className="text-[11px] text-fg-subtle">—</span>;
  if (d.arrow === 'new')  return <span className="text-[11px] font-medium text-emerald-500">new</span>;
  if (d.arrow === 'up')   return <span className="text-[11px] font-medium num text-emerald-500">↑ {d.pct}%</span>;
  return <span className="text-[11px] font-medium num text-rose-500">↓ {Math.abs(d.pct!)}%</span>;
}

// --- WALL ROW ---------------------------------------------------------------

function WallRowItem({ row }: { row: WallRow }) {
  const values = row.cells.map((c) => c.count);
  return (
    <Link
      to={`/profiles/${safeId(row.canonical_id)}`}
      className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-2.5 sm:gap-3 group py-1.5 px-2 -mx-2 rounded-md hover:bg-surface-hover transition-colors"
    >
      <Avatar name={row.display_name} login={row.primary_login} size={22} />
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-fg truncate">{row.display_name}</div>
      </div>
      <WallSpark values={values} />
      <span className="text-[12px] font-semibold text-fg tabular-nums num min-w-[2.25rem] text-right">
        {row.total}
      </span>
    </Link>
  );
}

function WallSpark({ values }: { values: number[] }) {
  const { line, area } = useMemo(
    () => smoothPath(values, { width: 200, height: 24, pad: 1.5 }),
    [values],
  );
  // On mobile the row has a hard width budget — the sparkline shrinks so
  // the name column gets real room (was truncating to one letter).

  return (
    <svg
      viewBox="0 0 200 24"
      preserveAspectRatio="none"
      height={24}
      className="block shrink-0 w-20 sm:w-[140px]"
      aria-hidden
    >
      <path d={area} fill="currentColor" fillOpacity="0.18" className="text-accent-500" />
      <path d={line} fill="none" stroke="currentColor" strokeWidth="1.25"
        strokeLinecap="round" strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        className="text-accent-500" />
    </svg>
  );
}

// --- BOARD CARD -------------------------------------------------------------

function BoardCard({ card }: { card: CardDef }) {
  return (
    <section className="card p-4">
      <header className="mb-3">
        <h2 className="text-[13px] font-semibold text-fg">{card.title}</h2>
        {card.subtitle && <p className="text-[11px] text-fg-subtle mt-0.5">{card.subtitle}</p>}
      </header>
      {card.rows.length === 0 ? (
        <p className="text-[12px] text-fg-subtle py-4 text-center">No activity yet.</p>
      ) : (
        <ol className="space-y-0.5">
          {card.rows.map((r) => <BoardRow key={r.canonical_id} r={r} />)}
        </ol>
      )}
    </section>
  );
}

function BoardRow({ r }: { r: CardRow }) {
  return (
    <li>
      <Link
        to={`/profiles/${safeId(r.canonical_id)}`}
        className="grid grid-cols-[1.25rem_auto_1fr_auto_auto] items-center gap-2.5 px-1.5 py-1.5 rounded-md hover:bg-surface-hover transition-colors"
      >
        <span className="text-[11px] font-semibold num text-fg-subtle">{String(r.rank).padStart(2, '0')}</span>
        <Avatar name={r.display_name} login={r.primary_login} size={22} />
        <div className="min-w-0">
          <PersonIdentity name={r.display_name} login={r.primary_login} size="sm" />
        </div>
        <Sparkline values={r.spark} width={52} height={14} />
        <span className="text-[13px] font-semibold num text-fg tabular-nums shrink-0 text-right min-w-[2.5rem]">
          {r.value}{r.valueSuffix ?? ''}
        </span>
      </Link>
    </li>
  );
}

// --- FEED ROW ---------------------------------------------------------------

function FeedRow({ e }: { e: FeedEvent }) {
  return (
    <li className="flex items-start gap-3 text-[13px]">
      <span className="text-[10px] font-semibold tracking-wide uppercase mt-0.5 shrink-0 text-emerald-500">
        merged
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <Link
            to={`/profiles/${safeId(e.canonical_id)}`}
            className="font-medium text-fg hover:text-accent-500 truncate"
          >
            {e.display_name}
          </Link>
          <span className="text-[11px] text-fg-subtle whitespace-nowrap num">
            {e.org}/{e.repo}#{e.number}
          </span>
          {e.is_bhai_assisted && (
            <span className="px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-500 text-[10px] uppercase tracking-wide">
              bhai{e.bhai_ticket_ref ? ` | ${e.bhai_ticket_ref}` : ''}
            </span>
          )}
          <span className="ml-auto text-[11px] text-fg-subtle whitespace-nowrap num">
            {formatRelative(e.date)}
          </span>
        </div>
        {e.title && <div className="text-fg-muted text-[12px] truncate">{e.title}</div>}
      </div>
    </li>
  );
}

